import * as React from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';

interface MultiSelectProps extends Pick<React.ButtonHTMLAttributes<HTMLButtonElement>, 'id' | 'autoFocus' | 'aria-label' | 'aria-labelledby' | 'aria-describedby'> {
  options: { label: string; value: string }[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  className?: string;
  /** Theme scope for the portaled dropdown. */
  contentClassName?: string;
  /** Override chip labels when menu labels carry additional context, such as counts. */
  getSelectedLabel?: (value: string) => string;
  /** Opt in for long lists inside a modal: its scroll lock otherwise blocks a body-portaled non-modal list. */
  modal?: boolean;
}

export const MultiSelect = React.forwardRef<HTMLButtonElement, MultiSelectProps>(
  ({ options, selected, onChange, placeholder = 'Select items...', className, contentClassName, getSelectedLabel, modal = false, ...triggerProps }, ref) => {
    const [open, setOpen] = React.useState(false);
    const triggerRef = React.useRef<HTMLButtonElement | null>(null);
    const removeRefs = React.useRef<Array<HTMLButtonElement | null>>([]);
    const pendingRemoval = React.useRef<number | null>(null);
    const setTriggerRef = React.useCallback((element: HTMLButtonElement | null) => {
      triggerRef.current = element;
      if (typeof ref === 'function') {
        ref(element);
      } else if (ref) {
        ref.current = element;
      }
    }, [ref]);

    React.useEffect(() => {
      if (pendingRemoval.current === null) {
        return;
      }
      const index = pendingRemoval.current;
      pendingRemoval.current = null;
      (removeRefs.current[Math.min(index, selected.length - 1)] ?? triggerRef.current)?.focus();
    }, [selected]);

    const chipLabel = (value: string) => getSelectedLabel?.(value) ?? options.find((option) => option.value === value)?.label ?? value;
    const handleSelect = (value: string) => {
      onChange(selected.includes(value) ? selected.filter((item) => item !== value) : [...selected, value]);
    };

    return (
      <Popover open={open} onOpenChange={setOpen} modal={modal}>
        <PopoverAnchor asChild>
          <div
            role="group"
            aria-label={triggerProps['aria-label'] ?? (triggerProps['aria-labelledby'] ? undefined : placeholder)}
            aria-labelledby={triggerProps['aria-labelledby']}
            data-slot="multi-select-field"
            className={cn('flex min-h-10 w-full cursor-pointer items-center gap-1 rounded-lg border border-input bg-card px-3 py-1 text-left text-sm shadow-sm focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/20', className)}
            onClick={(event) => {
              // Chips and trigger are siblings, so whitespace and chip text delegate to the native trigger.
              if (!(event.target as Element).closest('button')) {
                triggerRef.current?.click();
              }
            }}
          >
            {selected.length > 0 && (
              <div className="flex min-w-0 flex-1 flex-wrap gap-1">
                {selected.map((value, index) => (
                  <Badge key={value} variant="secondary" className="max-w-full gap-1 py-0 pr-0.5">
                    <span className="truncate">{chipLabel(value)}</span>
                    <button
                      ref={(element) => {
                        removeRefs.current[index] = element;
                      }}
                      type="button"
                      aria-label={`Remove ${chipLabel(value)}`}
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      onClick={(event) => {
                        event.stopPropagation();
                        pendingRemoval.current = index;
                        onChange(selected.filter((item) => item !== value));
                      }}
                    >
                      <X aria-hidden="true" className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
            <PopoverTrigger asChild>
              <button
                {...triggerProps}
                ref={setTriggerRef}
                type="button"
                aria-label={triggerProps['aria-label'] ?? (triggerProps['aria-labelledby'] ? undefined : placeholder)}
                className={cn('flex min-h-7 items-center justify-between gap-2 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring', selected.length ? 'shrink-0 px-1' : 'w-full')}
              >
                {selected.length === 0 && <span className="text-muted-foreground">{placeholder}</span>}
                <ChevronsUpDown aria-hidden="true" className="h-4 w-4 shrink-0 opacity-50" />
              </button>
            </PopoverTrigger>
          </div>
        </PopoverAnchor>
        <PopoverContent
          className={cn('p-0', contentClassName)} align="start" collisionPadding={8}
          aria-labelledby={triggerProps['aria-labelledby']}
          aria-label={triggerProps['aria-labelledby'] ? undefined : `${triggerProps['aria-label'] ?? placeholder} options`}
          style={{ width: 'var(--radix-popover-trigger-width)' }}
        >
          <Command>
            <CommandInput placeholder="Search..." aria-label={`Search ${triggerProps['aria-label'] ?? placeholder}`} />
            <CommandList style={{ maxHeight: 'min(300px, max(0px, calc(var(--radix-popover-content-available-height) - 48px)))' }}>
              <CommandEmpty>No item found.</CommandEmpty>
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem key={option.value} value={option.value} keywords={[option.label]} onSelect={() => handleSelect(option.value)}>
                    <Check aria-hidden="true" className={cn('mr-2 h-4 w-4', selected.includes(option.value) ? 'opacity-100' : 'opacity-0')} />
                    {option.label}
                    <span className="sr-only">{selected.includes(option.value) ? ' selected' : ' not selected'}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  }
);
MultiSelect.displayName = 'MultiSelect';
